import { useState } from "react";
import { motion } from "framer-motion";
import {
  Send,
  CheckCircle2,
  Globe2,
  Linkedin,
  Mail,
  MessageSquare,
  Briefcase,
  GraduationCap,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { api, SITE } from "@/lib/site";
import { getUTM } from "@/lib/utm";
import { toast } from "sonner";

const programs = [
  "Microsoft Azure for Architects",
  "AI Security & GenAI Defence",
  "Microsoft Sentinel for SecOps",
  "CISSP Bootcamp",
  "Microsoft Entra ID Mastery",
  "Multi-Cloud Security Architecture",
  "Custom corporate engagement",
];

const INQUIRY_TYPES = [
  {
    key: "consultation",
    label: "Consultation",
    sub: "Strategic 1:1 advisory",
    icon: Briefcase,
    tone: "from-sky-500/20 to-blue-500/5",
    border: "border-sky-500/30",
    accent: "text-sky-300",
  },
  {
    key: "corporate_workshop",
    label: "Corporate Workshop",
    sub: "Team enablement & bootcamps",
    icon: GraduationCap,
    tone: "from-emerald-500/20 to-emerald-500/5",
    border: "border-emerald-500/30",
    accent: "text-emerald-300",
  },
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    program: programs[0],
    inquiry_type: "consultation",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setInquiry = (key) => setForm((f) => ({ ...f, inquiry_type: key }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 10) {
      toast.error("Please share a few more details (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, utm: getUTM() });
      setSuccess(true);
      toast.success("Inquiry received — Ajay will respond within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        program: programs[0],
        inquiry_type: form.inquiry_type,
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : "Please check the form and try again."
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Ambient cyber/SOC backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 10%, rgba(56,189,248,0.10) 0%, transparent 60%), radial-gradient(50% 40% at 85% 90%, rgba(16,185,129,0.08) 0%, transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-white/30" />
              <span className="label-eyebrow">06 — Engage Ajay</span>
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white leading-[1.05]">
              Trusted by enterprise security & cloud leaders —{" "}
              <span className="text-slate-500">let's design your next program.</span>
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-xl">
              Whether it's a strategic advisory engagement or a full team
              enablement bootcamp — share a few details and I'll come back with
              a tailored proposal within{" "}
              <span className="text-white font-medium">24 hours</span>.
            </p>
          </div>

          {/* Trust strip */}
          <div className="flex flex-col gap-3">
            <Pill icon={Globe2} text="Delivered globally — remote & on-site" />
            <Pill icon={ShieldCheck} text="NDA-friendly · confidential intake" />
            <Pill icon={MessageSquare} text="24-hour response SLA" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Left rail — premium consult card */}
          <aside
            data-testid="contact-side-rail"
            className="lg:col-span-5 space-y-5"
          >
            {/* LinkedIn premium CTA card */}
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-linkedin-card"
              className="group relative block overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0A0F1C] via-[#0A0C13] to-[#080A10] p-6 hover:border-sky-500/40 transition-colors"
            >
              <div
                aria-hidden
                className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background:
                    "radial-gradient(60% 80% at 20% 0%, rgba(56,189,248,0.18) 0%, transparent 60%)",
                }}
              />
              <div className="relative flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sky-300/80 mb-2">
                    Direct line
                  </div>
                  <div className="flex items-center gap-2 text-white text-lg font-medium">
                    <Linkedin size={18} className="text-sky-400" />
                    Connect on LinkedIn
                  </div>
                  <p className="text-sm text-slate-400 mt-2 max-w-[26ch]">
                    Fastest path for enterprise stakeholders & procurement teams.
                  </p>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-slate-500 group-hover:text-sky-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </div>
            </a>

            {/* Channels grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ChannelCard
                testid="contact-email-link"
                href={`mailto:${SITE.email}`}
                icon={Mail}
                label="Email"
                value={SITE.email}
              />
              <ChannelCard
                testid="contact-whatsapp-link"
                href={SITE.whatsappLink}
                icon={MessageSquare}
                label="WhatsApp"
                value={SITE.whatsapp}
                external
              />
            </div>

            {/* Engagement modes */}
            <div className="rounded-2xl border border-white/10 bg-[#0A0C13] p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
                How we typically engage
              </div>
              <ul className="space-y-3 text-sm text-slate-300">
                {[
                  "Executive briefings & strategic security advisory",
                  "Multi-day instructor-led bootcamps (in-person / virtual)",
                  "Tailored corporate enablement for cloud & SOC teams",
                  "Long-term retainer for transformation programs",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-sky-400 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative lg:col-span-7 rounded-2xl border border-white/10 bg-[#0A0C13]/95 backdrop-blur p-7 md:p-9"
          >
            {/* Inquiry type selector */}
            <div className="mb-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">
                Inquiry type
              </div>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                role="radiogroup"
                aria-label="Inquiry type"
              >
                {INQUIRY_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.inquiry_type === t.key;
                  return (
                    <button
                      type="button"
                      key={t.key}
                      onClick={() => setInquiry(t.key)}
                      data-testid={`contact-inquiry-${t.key}`}
                      aria-pressed={active}
                      className={`relative text-left rounded-xl border p-4 transition-all overflow-hidden ${
                        active
                          ? `${t.border} bg-gradient-to-br ${t.tone}`
                          : "border-white/10 bg-white/[0.02] hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`h-9 w-9 rounded-lg flex items-center justify-center border ${
                            active
                              ? `${t.border} ${t.accent} bg-black/30`
                              : "border-white/10 text-slate-400"
                          }`}
                        >
                          <Icon size={16} />
                        </div>
                        <div>
                          <div className="text-white text-sm font-medium">
                            {t.label}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {t.sub}
                          </div>
                        </div>
                      </div>
                      {active && (
                        <span
                          aria-hidden
                          className={`absolute top-3 right-3 h-2 w-2 rounded-full ${
                            t.key === "consultation"
                              ? "bg-sky-400 shadow-[0_0_12px_2px_rgba(56,189,248,0.7)]"
                              : "bg-emerald-400 shadow-[0_0_12px_2px_rgba(16,185,129,0.7)]"
                          }`}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {success && (
                <div className="md:col-span-2 flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">
                  <CheckCircle2 size={18} />
                  <span className="text-sm">
                    Thank you. Your inquiry is received — expect a tailored
                    response within 24 hours.
                  </span>
                </div>
              )}
              <Field label="Full name" required>
                <input
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  data-testid="contact-input-name"
                  className="ak-input"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Work email" required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  data-testid="contact-input-email"
                  className="ak-input"
                  placeholder="jane@company.com"
                />
              </Field>
              <Field label="Company">
                <input
                  value={form.company}
                  onChange={onChange("company")}
                  data-testid="contact-input-company"
                  className="ak-input"
                  placeholder="Company Inc."
                />
              </Field>
              <Field label="Phone / WhatsApp">
                <input
                  value={form.phone}
                  onChange={onChange("phone")}
                  data-testid="contact-input-phone"
                  className="ak-input"
                  placeholder="+91 …"
                />
              </Field>
              <Field label="Program of interest" full>
                <select
                  value={form.program}
                  onChange={onChange("program")}
                  data-testid="contact-select-program"
                  className="ak-input"
                >
                  {programs.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={
                  form.inquiry_type === "corporate_workshop"
                    ? "Tell me about your team & objectives"
                    : "What outcome are you looking for?"
                }
                full
                required
              >
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange("message")}
                  data-testid="contact-input-message"
                  className="ak-input resize-none"
                  placeholder={
                    form.inquiry_type === "corporate_workshop"
                      ? "Team size, audience seniority, timeline, delivery format…"
                      : "The challenge you're solving, timelines, decision context…"
                  }
                />
              </Field>

              <div className="md:col-span-2 mt-1 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-white/5">
                <p className="text-[11px] text-slate-500 max-w-sm">
                  Your details are stored securely and used only to respond to
                  your inquiry. Routed directly to{" "}
                  <span className="text-slate-300">{SITE.email}</span>.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group relative inline-flex items-center justify-center gap-2 bg-white text-black font-medium px-7 py-3.5 rounded-full hover:bg-slate-200 disabled:opacity-60 transition-colors overflow-hidden"
                >
                  <span className="relative z-10">
                    {submitting ? "Sending…" : "Send inquiry"}
                  </span>
                  {!submitting && (
                    <Send
                      size={15}
                      className="relative z-10 group-hover:translate-x-0.5 transition-transform"
                    />
                  )}
                </button>
              </div>
            </div>

            <style>{`
              .ak-input {
                width: 100%;
                background: rgba(255,255,255,0.03);
                border: 1px solid rgba(255,255,255,0.10);
                color: #fff;
                border-radius: 0.75rem;
                padding: 0.85rem 1rem;
                font-size: 0.9rem;
                font-family: 'Satoshi', sans-serif;
                outline: none;
                transition: border 0.2s, background 0.2s, box-shadow 0.2s;
              }
              .ak-input:focus {
                border-color: rgba(56,189,248,0.55);
                background: rgba(255,255,255,0.05);
                box-shadow: 0 0 0 4px rgba(56,189,248,0.08);
              }
              .ak-input::placeholder { color: #64748B; }
              .ak-input option { background: #0A0C13; }
            `}</style>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Pill({ icon: Icon, text }) {
  return (
    <div className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-2 text-xs text-slate-300">
      <Icon size={14} className="text-sky-400" />
      <span>{text}</span>
    </div>
  );
}

function ChannelCard({ icon: Icon, label, value, href, external, testid }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
      className="group block rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/20 hover:bg-white/[0.04] transition-colors"
    >
      <div className="flex items-center gap-2 text-slate-400 mb-1.5">
        <Icon size={13} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          {label}
        </span>
      </div>
      <div className="text-white text-sm group-hover:text-sky-300 transition-colors truncate">
        {value}
      </div>
    </a>
  );
}

function Field({ label, children, required, full }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        {label} {required && <span className="text-sky-400">*</span>}
      </span>
      {children}
    </label>
  );
}
